import { Home, Key } from '@portals/icons';
import type { MarketingType, RawFilterConfigItem } from '@portals/sip-client-data/src/general/ApiClientTypes';
import { useFormikContext } from 'formik';
import { getFixedT } from 'i18next';
import React, { useCallback, useContext, useMemo } from 'react';

import { ControlledSelectField, type ControlledSelectFieldOptionsProps } from '../../../../../components';
import { getAllMarketingTypes } from '../../../../../config';
import { CatalogsContext } from '../../../../../context';
import { getEstateTypeIdOptions } from '../../../../../utils';

const t = getFixedT(null, 'core-immobilien');

type EstateTypeId = RawFilterConfigItem['estateTypeId'];
interface Props {
  estateTypeIdFieldName?: string;
  marketingTypeFieldName?: string;
}

export const MarketingTypeEstateTypeIdFilter: React.FC<Props> = ({
  estateTypeIdFieldName = 'estateTypeId',
  marketingTypeFieldName = 'marketingType',
}) => {
  const { values, setFieldTouched, setFieldValue } = useFormikContext();
  const { estateSubTypes: catalogsEstateSubTypes } = useContext(CatalogsContext);

  const estateTypeIdValue = values[estateTypeIdFieldName] as EstateTypeId;
  const marketingValue = values[marketingTypeFieldName] as MarketingType;

  const estateTypeOptions = useMemo(
    () => getEstateTypeIdOptions(catalogsEstateSubTypes, marketingValue) as ControlledSelectFieldOptionsProps[],
    [catalogsEstateSubTypes, marketingValue]
  );

  const onMarketingTypeChange = useCallback(
    (newMarketing: MarketingType) => {
      setFieldTouched(marketingTypeFieldName, true);
      setFieldValue(marketingTypeFieldName, newMarketing, true);

      const stillValid = estateTypeOptions.some((o) => o.value === estateTypeIdValue);
      const newEstateTypeId = stillValid
        ? estateTypeIdValue
        : estateTypeOptions.length > 0
        ? estateTypeOptions[0].value
        : '';
      setFieldTouched(estateTypeIdFieldName, true);
      setFieldValue(estateTypeIdFieldName, newEstateTypeId, true);
    },
    [
      estateTypeOptions,
      estateTypeIdFieldName,
      estateTypeIdValue,
      marketingTypeFieldName,
      setFieldTouched,
      setFieldValue,
    ]
  );

  const onEstateTypeIdChange = useCallback(
    (newEstateTypeId: EstateTypeId) => {
      setFieldTouched(estateTypeIdFieldName, true);
      setFieldValue(estateTypeIdFieldName, newEstateTypeId, true);
    },
    [estateTypeIdFieldName, setFieldTouched, setFieldValue]
  );

  return (
    <>
      <ControlledSelectField
        testId={marketingTypeFieldName}
        label={t('estateSearch.usageType')}
        icon={<Key />}
        items={getAllMarketingTypes(t) as ControlledSelectFieldOptionsProps[]}
        selectedValue={marketingValue}
        onChange={onMarketingTypeChange}
      />
      <ControlledSelectField
        testId={estateTypeIdFieldName}
        label={t('what')}
        icon={<Home />}
        items={estateTypeOptions}
        selectedValue={estateTypeIdValue}
        onChange={onEstateTypeIdChange}
      />
    </>
  );
};

MarketingTypeEstateTypeIdFilter.displayName = 'MarketingTypeEstateTypeIdFilter';
